import { Component, forwardRef, Input, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { SearchGroup } from "@core/data";
import { ReplaySubject, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";

@Component({
    selector: 'pv-filter-search',
    templateUrl: 'pv-filter-search.component.html',
    styleUrls: ['pv-filter-search.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PvFilterSearch),
            multi: true
        }
    ],
    host: {
        class: 'pv-filter-search'
    }
})
export class PvFilterSearch<T> implements OnInit, OnDestroy, ControlValueAccessor {

    //? used for UI feedback (e.g. No Matching <dataLabel> found)
    @Input()
    dataLabel: string;

    //? a custom template that can be passed on from the parent to act as custom option labels (see usage example below)
    @Input()
    optionTemplate: TemplateRef<any>;

    //? the property on your object you wish to search on
    @Input()
    searchLabel: keyof T;

    //? the unique key used to identify your objects
    @Input() idKey: string = 'id';

    @Input()
    isRequired: boolean = false;

    @Input()
    multiple: boolean = false;

    //? your search options, this needs to be a SearchGroup but the data can be of any type
    @Input()
    set options(value: SearchGroup<T>[]) {
        this._options = value;
        this.filterSearchGroups();
    }
    get options(): SearchGroup<T>[] {
        return this._options;
    }

    private _isWritingValue = false;
    private _options: SearchGroup<T>[] = [];
    private currentVisibleOptions: T[] = [];

    public innerControl = new FormControl(); // The internal control for mat-select
    public proxyControl = new FormControl(); // The buffer control for mat-select
    public groupFilterControl: FormControl = new FormControl();
    public filteredDataGroups: ReplaySubject<SearchGroup<T>[]> = new ReplaySubject<SearchGroup<T>[]>(1);
    private _destroy$ = new Subject();

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit() {
        if (this.isRequired) this.innerControl.setValidators([Validators.required]);

        this.groupFilterControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe(() => {
                this.filterSearchGroups();
            });

        this.proxyControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((newVisibleSelections) => {
                if (this._isWritingValue) return;

                if (!this.multiple) {
                    this.innerControl.setValue(newVisibleSelections);
                    return;
                }

                const previousSelections = this.innerControl.value || [];

                // Find items that are selected but currently hidden by the search filter
                const invisibleSelections = previousSelections.filter(prevItem =>
                    !this.currentVisibleOptions.some(visItem => this.compareWith(prevItem, visItem))
                );

                // Merge hidden selections with the newly checked visible selections
                const finalSelection = [...new Set([...invisibleSelections, ...(newVisibleSelections || [])])];

                // Update the real control (which notifies the parent)
                this.innerControl.setValue(finalSelection);
            });

        this.innerControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe(val => {
                this.onChange(val);
                this.proxyControl.setValue(val, { emitEvent: false });
            });
    }

    // 1. Angular calls this when the Parent writes a value (model -> view)
    writeValue(value: any): void {
        this._isWritingValue = true;

        const normalizedValue = this.multiple ? (value || []) : value;
        this.innerControl.setValue(normalizedValue, { emitEvent: false });
        this.proxyControl.setValue(normalizedValue);

        this._isWritingValue = false;
    }

    // 2. Angular gives us a function to call when the value changes (view -> model)
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    // 3. Angular gives us a function to call when the user touches the control
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    // 4. Handle disable state
    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.innerControl.disable({ emitEvent: false });
            this.proxyControl.disable({ emitEvent: false });
        } else {
            this.innerControl.enable({ emitEvent: false });
            this.proxyControl.enable({ emitEvent: false });
        }
    }

    trackById = (index: number, item: T) => {
        return item ? (item[this.idKey] || index) : index;
    }

    compareWith = (o1: any, o2: any) => {
        if (o1 === o2) return true;
        if (!o1 || !o2) return false;
        return o1[this.idKey] === o2[this.idKey];
    };

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    //? main filtering logic for the component
    filterSearchGroups() {
        if (!this.options) {
            this.currentVisibleOptions = [];
            return this.filteredDataGroups.next([]);
        }

        let search = this.groupFilterControl.value;

        if (!search) {
            this.currentVisibleOptions = this.options.reduce((acc, group) => acc.concat(group.data), []);
            return this.filteredDataGroups.next(this.options);
        }

        search = search.toLowerCase();

        const result = this.options.map(group => {
            const groupMatches = group.id.toLocaleLowerCase().includes(search);

            const filteredChildren = group.data.filter(item => {
                if (groupMatches) return true;

                const value = String(item[this.searchLabel]).toLowerCase();
                return value.includes(search);
            });

            return {
                ...group,
                data: filteredChildren,
            };
        }).filter(group => group.data.length > 0);

        this.currentVisibleOptions = result.reduce((acc, group) => acc.concat(group.data), []);
        this.filteredDataGroups.next(result);
    }
}

/**
 * Usage example
 *
 *  ? This is a custom template for a Measurement
    <ng-template #measureTemplate let-item>
        <span>{{ item.label }}</span>
        <small class="text-muted"> [{{ item.unit }}]</small>
    </ng-template>

 *  ? This is the usage of the actual component
    <pv-filter-search
        [options]="state.measureOptions"
        [formControl]="measureControl"
        [optionTemplate]="measureTemplate" <--- The template is passed in here
        dataLabel="Measurements"
        searchLabel="label"
    >
    </pv-filter-search>
 *
 */